/**
 * Whether the reader has changed something since it was last read or saved.
 *
 * The mirror image of `payloadChanged`, and deliberately a second function rather than a reuse of it,
 * because the two answer opposite questions and so must answer the unknown case in opposite ways:
 *
 * - A WRITE gate asks "might there be something to send". No baseline must mean YES — a wasted call
 *   costs a round trip, a skipped one costs the reader their edit. That is `payloadChanged`.
 * - A MARKER asks "did you change this". No baseline must mean NO — a mark on a section nobody
 *   touched is a claim the reader cannot check, and it is indistinguishable from a true one, so it
 *   makes every other mark on the page worth less.
 *
 * Calling `payloadChanged` for a marker is a one-word mistake that type-checks, and its symptom is a
 * page that says you have unsaved edits the moment you open it, before you have typed anything.
 *
 * Same `JSON.stringify` comparison, for the same reason: both sides are produced by one function, so
 * key order is identical by construction. Where a caller's two sides can legitimately arrive in
 * different orders — a set of names, a map keyed by uuid — it normalises before calling this, rather
 * than this trying to guess what an order-insensitive comparison would mean.
 */
export const editedSince = <T>(current: T, baseline: T | null | undefined): boolean =>
  baseline !== null && baseline !== undefined && JSON.stringify(current) !== JSON.stringify(baseline);

export default editedSince;
